import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  TrendingDown, 
  FileText,
  Bell,
  Menu,
  X,
  Wallet,
  MessageSquare,
  Megaphone,
  Settings,
  LogOut,
  CalendarClock,
  KeyRound,
  Eye,
  EyeOff,
  Clock,
  ClipboardList,
  Receipt,
  Coins,
  UserCheck,
  FileSpreadsheet,
  Smartphone,
  Database,
  FolderOpen,
  Calculator,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Wifi,
  WifiOff,
  MapPin,
  Check,
  CheckCheck
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accountingOpen, setAccountingOpen] = useState(true); // Accounting dropdown state
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef(null);
  
  // Fetch notifications on login
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axios.get(`${API}/notifications/unread`);
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.count || 0);
        
        // Show toast for unread notifications on first load
        if (response.data.count > 0) {
          toast.info(`You have ${response.data.count} unread notification${response.data.count > 1 ? 's' : ''}`, {
            duration: 5000,
            action: {
              label: 'View',
              onClick: () => setShowNotifications(true)
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };
    
    if (user) {
      fetchNotifications();
    }
  }, [user]);
  
  // Close notifications dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };
  
  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };
  
  // Handle notification click
  const handleNotificationClick = async (notification) => {
    await markAsRead(notification.id);
    setShowNotifications(false);
    if (notification.link) {
      navigate(notification.link);
    }
  };
  
  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  // Check if user can see NAS (for back button visibility)
  const canAccessNAS = user?.role === 'admin' || user?.nas_access === true;
  
  const handleBackToApps = () => {
    localStorage.removeItem('selectedApp');
    navigate('/select-app');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully!');
      setIsPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  // Build navigation items based on user role
  let navItems = [];
  
  // List of staff users who have full view access
  const fullAccessStaff = ['alma', 'jelyn', 'pabs', 'janecarla', 'kristine', 'nicole'];
  const username = user?.username?.toLowerCase() || '';
  const fullName = user?.full_name?.toLowerCase().replace(/\s+/g, '') || '';
  
  // Check if current user is in the full access list
  const hasFullAccess = user?.role === 'admin' || 
    fullAccessStaff.some(name => username.includes(name) || fullName.includes(name));
  
  if (hasFullAccess) {
    // Admin and specified staff see everything
    navItems = [
      { name: 'Dashboard', path: '/arbe/dashboard', icon: LayoutDashboard },
      { name: 'Subscribers', path: '/arbe/subscribers', icon: Users },
      { name: 'Due Dates', path: '/arbe/due-dates', icon: CalendarClock },
      { name: 'Payments', path: '/arbe/payments', icon: CreditCard },
      { name: 'Expenses', path: '/arbe/expenses', icon: TrendingDown },
      { name: 'Invoices', path: '/arbe/invoices', icon: FileText },
      { name: 'Reminders', path: '/arbe/reminders', icon: Bell },
      { name: 'Vendo Machine', path: '/arbe/vendo', icon: Coins },
      // Accounting items moved to dropdown
      { 
        name: 'Accounting', 
        icon: Calculator, 
        isDropdown: true,
        subItems: [
          { name: 'Time In/Out', path: '/arbe/time-clock', icon: Clock },
          { name: 'Attendance Dashboard', path: '/arbe/time-clock-admin', icon: Users, adminOnly: true },
          { name: 'Office Locations', path: '/arbe/office-locations', icon: MapPin, adminOnly: true },
          { name: 'Overtime Management', path: '/arbe/overtime', icon: Clock, adminOnly: true },
          { name: 'Timesheet Management', path: '/arbe/timesheet', icon: Clock },
          { name: 'Payroll Summary', path: '/arbe/payroll-summary', icon: FileSpreadsheet },
          { name: 'Cash Advance', path: '/arbe/cash-advance', icon: Wallet },
          { name: 'Payslips', path: '/arbe/payslips', icon: Receipt },
          { name: 'Payslip Archive', path: '/arbe/payslip-archive', icon: FolderOpen },
        ]
      },
      { name: 'Support', path: '/arbe/support', icon: MessageSquare },
      { name: 'Announcements', path: '/arbe/announcements', icon: Megaphone },
    ];
    
    // Only admin can see Users and Backup pages
    if (user?.role === 'admin') {
      navItems.push({ name: 'Users', path: '/arbe/users', icon: Settings });
      navItems.push({ name: 'Backup & Restore', path: '/arbe/backup', icon: Database });
    }
  } else {
    // Other staff sees basic pages including Cash Advance and Payslip Archive
    navItems = [
      { 
        name: 'Accounting', 
        icon: Calculator, 
        isDropdown: true,
        subItems: [
          { name: 'Time In/Out', path: '/arbe/time-clock', icon: Clock },
          { name: 'My Timesheet', path: '/arbe/timesheet', icon: Clock },
          { name: 'Cash Advance', path: '/arbe/cash-advance', icon: Wallet },
          { name: 'My Payslips', path: '/arbe/payslips', icon: Receipt },
          { name: 'Payslip Archive', path: '/arbe/payslip-archive', icon: FolderOpen },
        ]
      },
    ];
  }

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/arbe/dashboard') return location.pathname === '/arbe/dashboard' || location.pathname === '/arbe' || location.pathname === '/arbe/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-slate-900 text-slate-300 h-screen">
        <div className="p-6 border-b border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3" data-testid="app-title">
            <div className="relative w-12 h-12">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-2 border-blue-400">
                <span className="text-white font-jakarta font-bold text-xs tracking-tight">ARBE</span>
              </div>
              <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 ${isOnline ? 'bg-green-500' : 'bg-red-500'} rounded-full border-2 border-slate-900 flex items-center justify-center`}>
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-jakarta font-bold text-white">ARBE</h1>
              <p className="text-xs text-slate-400">Mipay Business Management</p>
            </div>
          </div>
        </div>
        
        {/* Back to Apps Button - Only show if user has NAS access */}
        {canAccessNAS && (
          <div className="px-4 py-2 border-b border-slate-800">
            <button
              onClick={handleBackToApps}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-white transition-all"
              data-testid="back-to-apps-btn"
            >
              <ChevronLeft size={16} />
              <span>Back to Apps</span>
            </button>
          </div>
        )}
        
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            
            // Handle dropdown items (Accounting)
            if (item.isDropdown) {
              const isAnySubActive = item.subItems.some(sub => isActive(sub.path));
              return (
                <div key={item.name} className="space-y-1">
                  <button
                    onClick={() => setAccountingOpen(!accountingOpen)}
                    data-testid={`nav-${item.name.toLowerCase()}`}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                      isAnySubActive
                        ? 'bg-blue-600/20 text-blue-400'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon size={20} />
                      <span>{item.name}</span>
                    </div>
                    {accountingOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </button>
                  
                  {accountingOpen && (
                    <div className="ml-4 pl-4 border-l border-slate-700 space-y-1">
                      {item.subItems
                        .filter(subItem => !subItem.adminOnly || user?.role === 'admin')
                        .map((subItem) => {
                        const SubIcon = subItem.icon;
                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            data-testid={`nav-${subItem.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium transition-all text-sm ${
                              isActive(subItem.path)
                                ? 'bg-blue-600 text-white shadow-lg'
                                : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                            }`}
                          >
                            <SubIcon size={18} />
                            <span>{subItem.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            
            // Regular nav items
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.name.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all active:scale-95 ${
                  isActive(item.path)
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* Mobile App / Notifications Button */}
        <div className="px-4 pb-2 flex-shrink-0">
          <Link
            to="/arbe/mobile-app"
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-all active:scale-95"
            data-testid="mobile-app-btn"
          >
            <Smartphone size={20} />
            <span>Mobile App</span>
          </Link>
        </div>
        
        {/* Change Password Button */}
        <div className="px-4 pb-2 flex-shrink-0">
          <button
            onClick={() => setIsPasswordDialogOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-all active:scale-95"
            data-testid="change-password-btn"
          >
            <KeyRound size={20} />
            <span>Change Password</span>
          </button>
        </div>
        
        {/* Logout Button - Fixed at bottom */}
        <div className="p-4 border-t border-slate-800 flex-shrink-0">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-red-600 hover:text-white transition-all active:scale-95"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
        
        {/* User Info */}
        <div className="p-4 border-t border-slate-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{user?.full_name?.charAt(0) || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.role}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-slate-900 text-slate-300 flex flex-col">
            <div className="p-6 border-b border-slate-800 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="relative w-12 h-12">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-2 border-blue-400">
                    <span className="text-white font-jakarta font-bold text-xs tracking-tight">Mipay</span>
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-slate-900 flex items-center justify-center">
                    <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-jakarta font-bold text-white">Mipay</h1>
                  <p className="text-xs text-slate-400">Subscription Management</p>
                </div>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                
                // Handle dropdown items (Accounting) for mobile
                if (item.isDropdown) {
                  const isAnySubActive = item.subItems.some(sub => isActive(sub.path));
                  return (
                    <div key={item.name} className="space-y-1">
                      <button
                        onClick={() => setAccountingOpen(!accountingOpen)}
                        className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                          isAnySubActive
                            ? 'bg-blue-600/20 text-blue-400'
                            : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon size={20} />
                          <span>{item.name}</span>
                        </div>
                        {accountingOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                      
                      {accountingOpen && (
                        <div className="ml-4 pl-4 border-l border-slate-700 space-y-1">
                          {item.subItems
                            .filter(subItem => !subItem.adminOnly || user?.role === 'admin')
                            .map((subItem) => {
                            const SubIcon = subItem.icon;
                            return (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                onClick={() => setSidebarOpen(false)}
                                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg font-medium transition-all text-sm ${
                                  isActive(subItem.path)
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                                }`}
                              >
                                <SubIcon size={18} />
                                <span>{subItem.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                      isActive(item.path)
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>
            
            {/* Mobile App Button - Mobile */}
            <div className="px-4 pb-2 flex-shrink-0">
              <Link
                to="/mobile-app"
                onClick={() => setSidebarOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
                data-testid="mobile-app-btn-mobile"
              >
                <Smartphone size={20} />
                <span>Mobile App</span>
              </Link>
            </div>
            
            {/* Change Password Button - Mobile */}
            <div className="px-4 pb-2 flex-shrink-0">
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  setIsPasswordDialogOpen(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-slate-800 hover:text-white transition-all"
              >
                <KeyRound size={20} />
                <span>Change Password</span>
              </button>
            </div>
            
            {/* Logout Button - Fixed at bottom */}
            <div className="p-4 border-t border-slate-800 flex-shrink-0">
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium text-slate-300 hover:bg-red-600 hover:text-white transition-all"
              >
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            </div>
            
            {/* User Info - Mobile */}
            <div className="p-4 border-t border-slate-800 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">{user?.full_name?.charAt(0) || 'U'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.role}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-testid="header">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-slate-600 hover:text-slate-900"
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
          <div className="flex-1 lg:flex-none">
            <h2 className="text-xl font-jakarta font-bold text-slate-900" data-testid="page-title">
              {navItems.find(item => isActive(item.path))?.name || 'Dashboard'}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            {/* Notifications Dropdown */}
            <div className="relative" ref={notificationRef}>
              <button 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors" 
                data-testid="notifications-btn"
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold px-1">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              
              {/* Notifications Dropdown Panel */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notifications-dropdown">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <button 
                        onClick={markAllAsRead}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        data-testid="mark-all-read-btn"
                      >
                        <CheckCheck size={14} />
                        Mark all read
                      </button>
                    )}
                  </div>
                  
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-slate-500">
                        <Bell size={32} className="mx-auto mb-2 text-slate-300" />
                        <p>No new notifications</p>
                      </div>
                    ) : (
                      notifications.map((notification) => (
                        <button
                          key={notification.id}
                          onClick={() => handleNotificationClick(notification)}
                          className="w-full px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 text-left transition-colors"
                          data-testid={`notification-${notification.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${
                              notification.type === 'timesheet' ? 'bg-blue-500' :
                              notification.type === 'payslip' ? 'bg-green-500' :
                              notification.type === 'warning' ? 'bg-amber-500' :
                              'bg-slate-400'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-slate-900 truncate">{notification.title}</p>
                              <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notification.message}</p>
                              <p className="text-xs text-slate-400 mt-1">
                                {new Date(notification.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <Check size={16} className="text-slate-400 hover:text-green-500 flex-shrink-0" />
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-center">
                      <Link 
                        to="/arbe/timesheet" 
                        onClick={() => setShowNotifications(false)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Go to Timesheets
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8 lg:p-10" data-testid="main-content">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and a new password to change it.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password *</Label>
              <div className="relative">
                <Input
                  id="currentPassword"
                  type={showPasswords.current ? "text" : "password"}
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  required
                  placeholder="Enter current password"
                  className="pr-10"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="newPassword">New Password *</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  required
                  placeholder="Enter new password"
                  className="pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password *</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required
                  placeholder="Confirm new password"
                  className="pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setIsPasswordDialogOpen(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setShowPasswords({ current: false, new: false, confirm: false });
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-slate-900 hover:bg-slate-800"
                disabled={changingPassword}
                data-testid="submit-password-btn"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;